import "./security.css";
import { CheckCircleIcon, KeyIcon } from "@phosphor-icons/react";
import { useId, useState, type FormEvent } from "react";
import type { AccountSecurityManagement } from "../types";
import { authenticationActionError } from "./presentation";

export function AuthenticationMethodsCard({
  accountSecurity,
}: {
  accountSecurity: AccountSecurityManagement;
}) {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackTone, setFeedbackTone] = useState<"error" | "success">(
    "success",
  );
  const [newPassword, setNewPassword] = useState("");
  const [pending, setPending] = useState<"password" | null>(null);
  const feedbackId = useId();
  const snapshot = accountSecurity.snapshot;

  async function establishPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword.length < 8) {
      setFeedbackTone("error");
      setFeedback("Use at least 8 characters.");
      return;
    }
    if (newPassword.length > 256) {
      setFeedbackTone("error");
      setFeedback("Use no more than 256 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFeedbackTone("error");
      setFeedback("Passwords do not match.");
      return;
    }
    setPending("password");
    setFeedback("");
    try {
      await accountSecurity.establishPassword(newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setFeedbackTone("success");
      setFeedback("Password added. You can now sign in with it.");
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(cause, "Your password could not be added."),
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="pm-passport-card pm-auth-methods-card">
      <header>
        <div>
          <h2>Sign-in methods</h2>
          <p>Keep at least one trusted way back into your Account.</p>
        </div>
        <small>Fresh sign-in required</small>
      </header>

      {accountSecurity.state === "loading" ? (
        <p className="pm-security-state" role="status">
          Checking your sign-in methods…
        </p>
      ) : accountSecurity.state === "unavailable" || snapshot === undefined ? (
        <div className="pm-security-state" role="alert">
          <strong>Security details are taking a cosmic detour.</strong>
          <span>
            Your existing sign-in methods have not changed. Try again shortly.
          </span>
          <button onClick={() => void accountSecurity.refresh()} type="button">
            Try again
          </button>
        </div>
      ) : (
        <div className="pm-auth-methods">
          <div className="pm-auth-method pm-auth-primary-email">
            <span className="pm-auth-method-icon">
              <CheckCircleIcon size={21} weight="fill" />
            </span>
            <div>
              <strong>Verified email</strong>
              <p>{snapshot.email}</p>
            </div>
            <small>
              {snapshot.emailVerified ? "Verified" : "Verification needed"}
            </small>
          </div>

          <div className="pm-auth-method">
            <span className="pm-auth-method-icon">
              <KeyIcon size={21} weight="duotone" />
            </span>
            <div>
              <strong>Password</strong>
              <p>
                {snapshot.methods.password
                  ? "Available for direct sign-in and account recovery."
                  : "Add a passphrase as a dependable way in."}
              </p>
            </div>
            {snapshot.methods.password ? <small>Ready</small> : null}
          </div>

          {!snapshot.methods.password ? (
            <form
              className="pm-establish-password"
              onSubmit={(event) => void establishPassword(event)}
            >
              <input
                aria-hidden="true"
                autoComplete="username"
                className="sr-only"
                name="username"
                readOnly
                tabIndex={-1}
                type="email"
                value={snapshot.email}
              />
              <label>
                <span>NEW PASSPHRASE</span>
                <input
                  aria-describedby={feedback ? feedbackId : undefined}
                  aria-invalid={
                    feedbackTone === "error" && feedback ? true : undefined
                  }
                  autoComplete="new-password"
                  disabled={pending !== null}
                  maxLength={256}
                  minLength={8}
                  onChange={(event) => {
                    setNewPassword(event.currentTarget.value);
                    setFeedback("");
                  }}
                  required
                  type="password"
                  value={newPassword}
                />
              </label>
              <label>
                <span>CONFIRM PASSPHRASE</span>
                <input
                  aria-describedby={feedback ? feedbackId : undefined}
                  aria-invalid={
                    feedbackTone === "error" && feedback ? true : undefined
                  }
                  autoComplete="new-password"
                  disabled={pending !== null}
                  maxLength={256}
                  minLength={8}
                  onChange={(event) => {
                    setConfirmPassword(event.currentTarget.value);
                    setFeedback("");
                  }}
                  required
                  type="password"
                  value={confirmPassword}
                />
              </label>
              <button disabled={pending !== null} type="submit">
                {pending === "password" ? "Adding password…" : "Add password"}
              </button>
            </form>
          ) : null}
        </div>
      )}

      {feedback ? (
        <p
          className={`pm-security-feedback ${feedbackTone}`}
          id={feedbackId}
          role={feedbackTone === "error" ? "alert" : "status"}
        >
          {feedback}
        </p>
      ) : null}
    </section>
  );
}
